import { Dropdown, Menu, Table, Tag, Tooltip } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import ThreeDot from 'icons/Threedot.svg';
import React, { FC, useMemo } from 'react';
import {
    ApiTokenRequestDto,
    ApiTokenRequestJournalType,
    ApiTokenRequestStatus,
} from '../types';
import {
    API_TOKEN_REQUEST_STATUS_COLORS,
    API_TOKEN_REQUEST_STATUS_LABELS,
} from '../utils/constants';
import { formatTokenRequestDate } from '../utils/token-request-helpers';

interface TokenRequestsTableProps {
    journalType: ApiTokenRequestJournalType;
    data: ApiTokenRequestDto[];
    isLoading: boolean;
    pagination: TablePaginationConfig;
    canManage: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (_current: number, size: number) => void;
    onView: (request: ApiTokenRequestDto) => void;
    onChangeStatus: (
        request: ApiTokenRequestDto,
        status?: Exclude<ApiTokenRequestStatus, 'TOKEN_ISSUED'>
    ) => void;
    onIssue: (request: ApiTokenRequestDto) => void;
}

const renderMessage = (message?: string | null) => {
    if (!message) {
        return '—';
    }

    return (
        <Tooltip title={message}>
            <div className="admin-token-generations__message-cell">{message}</div>
        </Tooltip>
    );
};

export const TokenRequestsTable: FC<TokenRequestsTableProps> = ({
    journalType,
    data,
    isLoading,
    pagination,
    canManage,
    onPageChange,
    onPageSizeChange,
    onView,
    onChangeStatus,
    onIssue,
}) => {
    const columns = useMemo<ColumnsType<ApiTokenRequestDto>>(() => {
        const commonColumns: ColumnsType<ApiTokenRequestDto> = [
            {
                title: '№ обращения',
                dataIndex: 'requestNumber',
                width: 150,
                fixed: 'left',
            },
            {
                title: journalType === 'ISSUED' ? 'Дата выдачи' : 'Дата подачи',
                dataIndex: journalType === 'ISSUED' ? 'updatedDate' : 'createdDate',
                width: 155,
                render: (value: string) => formatTokenRequestDate(value),
            },
            {
                title: 'ИИН/БИН',
                dataIndex: 'iinBin',
                width: 140,
            },
            {
                title: 'ФИО / Наименование',
                dataIndex: 'fullName',
                width: 240,
            },
        ];

        const submittedColumns: ColumnsType<ApiTokenRequestDto> = [
            {
                title: 'Номер телефона',
                dataIndex: 'phone',
                width: 170,
                render: (value: string) => value || '—',
            },
            {
                title: 'Электронная почта',
                dataIndex: 'email',
                width: 220,
                render: (value: string) => value || '—',
            },
            {
                title: 'Сообщение',
                dataIndex: 'message',
                width: 260,
                render: renderMessage,
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                width: 145,
                render: (status: ApiTokenRequestStatus) => (
                    <Tag color={API_TOKEN_REQUEST_STATUS_COLORS[status]}>
                        {API_TOKEN_REQUEST_STATUS_LABELS[status]}
                    </Tag>
                ),
            },
        ];

        const issuedColumns: ColumnsType<ApiTokenRequestDto> = [
            {
                title: 'Статус получения токена',
                dataIndex: 'tokenReceivedDate',
                width: 190,
                render: (tokenReceivedDate: string | null) => (
                    <Tag color={tokenReceivedDate ? 'green' : 'orange'}>
                        {tokenReceivedDate ? 'Получен' : 'Не получен'}
                    </Tag>
                ),
            },
            {
                title: 'Сообщение',
                dataIndex: 'message',
                width: 280,
                render: renderMessage,
            },
        ];

        const tailColumns: ColumnsType<ApiTokenRequestDto> = [
            {
                title: 'Вложения',
                dataIndex: 'attachments',
                width: 120,
                render: (attachments) =>
                    attachments?.length ? `${attachments.length} файл(а)` : 'Нет',
            },
            {
                title: 'Действие',
                width: 100,
                fixed: 'right',
                render: (_text, record) => {
                    const statusEditable = record.status !== 'TOKEN_ISSUED';

                    return (
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <Menu>
                                    <Menu.Item key="view" onClick={() => onView(record)}>
                                        Просмотреть
                                    </Menu.Item>
                                    {record.attachments?.length ? (
                                        <Menu.Item
                                            key="attachments"
                                            onClick={() => onView(record)}
                                        >
                                            Открыть вложения
                                        </Menu.Item>
                                    ) : null}
                                    {canManage && statusEditable ? (
                                        <>
                                            <Menu.Divider />
                                            <Menu.Item
                                                key="review"
                                                disabled={record.status === 'IN_REVIEW'}
                                                onClick={() =>
                                                    onChangeStatus(record, 'IN_REVIEW')
                                                }
                                            >
                                                Взять в обработку
                                            </Menu.Item>
                                            <Menu.Item
                                                key="approve"
                                                disabled={record.status === 'APPROVED'}
                                                onClick={() =>
                                                    onChangeStatus(record, 'APPROVED')
                                                }
                                            >
                                                Согласовать
                                            </Menu.Item>
                                            <Menu.Item
                                                key="reject"
                                                disabled={record.status === 'REJECTED'}
                                                onClick={() =>
                                                    onChangeStatus(record, 'REJECTED')
                                                }
                                            >
                                                Отказать
                                            </Menu.Item>
                                        </>
                                    ) : null}
                                    {canManage && record.status === 'APPROVED' ? (
                                        <>
                                            <Menu.Divider />
                                            <Menu.Item
                                                key="issue"
                                                onClick={() => onIssue(record)}
                                            >
                                                Выдать токен
                                            </Menu.Item>
                                        </>
                                    ) : null}
                                </Menu>
                            }
                        >
                            <span className="admin-token-generations__actions">
                                <ThreeDot />
                            </span>
                        </Dropdown>
                    );
                },
            },
        ];

        return [
            ...commonColumns,
            ...(journalType === 'ISSUED' ? issuedColumns : submittedColumns),
            ...tailColumns,
        ];
    }, [canManage, journalType, onChangeStatus, onIssue, onView]);

    return (
        <Table
            className="admin-token-generations__table"
            rowKey="id"
            dataSource={data}
            columns={columns}
            loading={isLoading}
            pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                onChange: onPageChange,
                onShowSizeChange: onPageSizeChange,
            }}
            scroll={{ x: journalType === 'ISSUED' ? 1350 : 1900 }}
        />
    );
};